export type ImageSize = {
  width: number;
  height: number;
  link: string;
}

export interface Image {
  id: string;
  sizes: ImageSize[];
}

export type UserSettings = {
  language: string;
  birthday: Date | null;
  countryCode: string | null;
  city: string | null;
  showBookings?: "none" | "anonymous" | "public" | undefined;
  showTgUsername?: boolean;
  showGiftedWishes?: boolean;
  tgUsername?: string | null;
}

export interface User extends UserSettings {
  id: string;
  shortId: string;
  fullname: string;
  photo: Image | null;
}

export type Currency = {
  code: string;
  symbol: string;
}

export const Currencies: Currency[] = [
  { code: "RUB", symbol: "₽" },
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "UAH", symbol: "₴" },
  { code: "TRY", symbol: "₺" },
  { code: "IDR", symbol: "Rp" },
  { code: "KZT", symbol: "₸" },
  { code: "BRL", symbol: "R$" },
  { code: "CNY", symbol: "¥" },
  { code: "UZS", symbol: "so'm" },
  { code: "KGS", symbol: "сом" },
  { code: "THB", symbol: "฿" },
  { code: "BYN", symbol: "Br" },
  { code: "GEL", symbol: "₾" },
]

export type Amount = {
  currency: string;
  number: number;
};

export type WishInfo = {
  title: string;
  description?: string | null;
  wishlistIds?: string[];
  url?: string | null;
  displayUrl?: string | null;
  price?: Amount | null;
  priceText?: string | null;
  image?: Image | null;
}

export type GiftedWishInfo = {
  giftedAt?: Date | null;
  giftedComment?: string | null;
  showGiftedBy?: boolean;
}

export interface Wish extends WishInfo {
  id: string;
  userId: string;
  bookedBy: null | "currentUser" | "otherUser";
  bookedByUser: User | null;
  bookedInSecretSantaGame?: { id: string; title: string };
  randomGradient: string;
  status: "active" | "gifted";
  giftedAt: Date | null | undefined;
  giftedComment: string | null | undefined;
  showGiftedBy: boolean | null | undefined;
  isCopied: boolean | null | undefined;
}

export interface Idea {
  id: string;
  title: string;
  description?: string | null;
  url?: string | null;
  priceText?: string | null;
  image?: Image | null;
}

export type WishlistInfo = {
  title: string;
  cover: Image | null;
  description: string | null;
  access: "public"| "link" | "private";
  wishIds: string[] | null;
}

export interface Wishlist extends WishlistInfo {
  id: string;
  shortId: string;
  owner: User;
  isSubscribed: boolean;
  wishes: Wish[];
}

export interface WishlistPreview extends Wishlist {
  wishesCount: number;
}

export type Profile = {
  user: User;
  wishlistPreviews: WishlistPreview[];
  ideas: Idea[];
}

export interface AuthProfile extends Profile {
  activeSecretSantaGames: SecretSantaGame[];
  allWishes: Wish[];
  subscribedWishlistPreviews: WishlistPreview[];
}

// ==========================================
// Secret Santa Domain Types
// ==========================================

export type SecretSantaGameStatus = "open" | "drawn" | "completed";

export type SecretSantaGameInfo = {
  title: string;
  description?: string | null;
  budget?: Amount | null;
  event_date?: Date | null;
  cover_image_id?: string | null;
}

export type ParticipantInfo = {
  display_name: string;
  wishlist_id: string;
  preferences?: string | null;
}

export interface SecretSantaParticipant extends ParticipantInfo {
  user_id: string;
  receiver_id?: string | null;
  joined_at: Date;
  receiverWishlist?: Wishlist;
}

export interface SecretSantaGame extends SecretSantaGameInfo {
  id: string;
  short_id: number;
  organizer_id: string;
  status: SecretSantaGameStatus;
  created_at: Date;
  updated_at: Date;
  participants: SecretSantaParticipant[];
  isOrganizer?: boolean;
  isParticipant?: boolean;
  myParticipant?: SecretSantaParticipant;
}

export type SecretSantaConversation = 'santa' | 'recipient';

export interface SecretSantaMessage {
  id: string;
  game_id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  sent_at: Date;
  isFromMe?: boolean;
}
